import React, { useEffect, useRef, useState } from "react";
import {
  MDBCheckbox,
  MDBInput, 
  MDBRadio,
  MDBSideNavCollapse,
  MDBSideNavItem,
  MDBSideNavLink,
  MDBTooltip,
  MDBIcon
} from "mdb-react-ui-kit";

const BillFilter = ({
  selectedBills,
  setSelectedBills,
  selectedBillTypes,
  setSelectedBillTypes,
  selectedBillStatus,
  setSelectedBillStatus,
  billCollapse,
  setBillCollapse,
  filteredCommittees,
}) => {
  const [billTypes] = useState([{
      enum: "HB",
      name: "House Bill",
      exportValue: "All HBs",
    }, {
      enum: "HJ",
      name: "House Joint Resolutions",
      exportValue: "All HJs",
    }, {
      enum: "HR",
      name: "House Resolutions",
      exportValue: "All HRs",
    }, {
      enum: "SB",
      name: "Senate Bill",
      exportValue: "All SBs",
    }, {
      enum: "SJ",
      name: "Senate Joint Resolutions",
      exportValue: "All SJs",
    }, {
      enum: "SR",
      name: "Senate Resolutions",
      exportValue: "All SRs",
    }
  ]);

  const [searchBill, setSearchBill] = useState("");
  const [initialized, setInitialized] = useState(false);
  const displayBillsRef = useRef([]);
  const [billList, setBillList] = useState([]);

  useEffect(() => {
    if (!initialized && filteredCommittees.length > 0) {
      displayBillsRef.current = [...filteredCommittees];
      setBillList(generateBillSelections());
      setInitialized(true);
    }
  }, [filteredCommittees, initialized]);

  const generateBillSelections = () => {
    const bills = [];

    displayBillsRef.current.forEach((committee) => {
      if (committee.meetingTimes && committee.meetingTimes.length > 0)
        committee.meetingTimes.forEach((meetingTime) => {
          if (meetingTime.bills && meetingTime.bills.length > 0) {
            meetingTime.bills.forEach((bill) => {
              if (
                !bills.some(
                  (listBill) => listBill.billNumber === bill.billNumber
                )
              )
                bills.push(bill);
            });
          }
        });
    });

    bills.sort((billA, billB) => {
      const codeA = billA.billNumber.split(" ")[0];
      const codeB = billB.billNumber.split(" ")[0];
      const numA = parseInt(billA.billNumber.split(" ")[1]);
      const numB = parseInt(billB.billNumber.split(" ")[1]);

      if (codeA > codeB) return 1;
      if (codeA < codeB) return -1;
      if (numA > numB) return 1;
      if (numA < numB) return -1;
      return 0;
    });

    return bills;
  };

  const handleBillTypeChange = (billType) => {
    setSelectedBillTypes((prev) => {
      if (prev.some((selected) => selected.enum === billType.enum)) {
        return prev.filter((selected) => selected.enum !== billType.enum);
      } else {
        return [
          ...prev,
          {
            enum: billType.enum,
            name: billType.name,
            exportValue: `${billType.exportValue}`,
          },
        ];
      }
    });
  };
  const handleBillChange = (bill) => {
    setSelectedBills((prev) => {
      if (prev.some((selected) => selected.billNumber === bill.billNumber)) {
        return prev.filter(
          (selected) => selected.billNumber !== bill.billNumber
        );
      } else {
        return [
          ...prev,
          {
            enum: bill.billNumber.split(" ")[0],
            billNumber: bill.billNumber,
            exportValue: `${bill.billNumber}`,
          },
        ];
      }
    });
  };

  return (
    <MDBSideNavItem>
      <MDBSideNavLink
        className="accordion-button"
        icon="angle-down"
        shouldBeExpanded={billCollapse}
        onClick={() => setBillCollapse(!billCollapse)}
      >
        Bill
      </MDBSideNavLink>
      <MDBSideNavCollapse open={billCollapse}>
        <li className="p-3">
          <fieldset className="mb-3 d-flex justify-content-between">
            <legend>Type</legend>
            <div className="row row-cols-1 row-cols-md-3 g-1">
              {billTypes.map((billType) => (
                  <div className="col" title={billType.name} key={"div_" + billType.enum}>
                    <MDBCheckbox
                        key={billType.enum}
                        label={billType.enum}
                        onChange={() => handleBillTypeChange(billType)}
                        checked={selectedBillTypes.some(
                            (selected) => selected.enum === billType.enum
                        )}
                    />
                  </div>
              ))}
            </div>
          </fieldset>
          <div className="mb-2">
            <div className='d-flex justify-content-between'>
              <p className="mb-0">Status</p>
              <div className="text-end">
                <MDBTooltip
                  title='By default, filters show current and future items. Adjust the timeframe filter to view past records. "Referred" means the item has been referred but isn’t scheduled for a hearing, while "Hearing" indicates a hearing is scheduled or has already occurred.'
                  tag="a"
                  color="none"
                >
                  <MDBIcon far icon="question-circle" />
                </MDBTooltip>
              </div>
            </div>
            <MDBRadio inline
                key={"Scheduled"}
                label={"Scheduled"}
                onChange={() => setSelectedBillStatus(true)}
                checked={selectedBillStatus}
            />
            <MDBRadio inline
                key={"Referred"}
                label={"Referred"}
                onChange={() => setSelectedBillStatus(false)}
                checked={!selectedBillStatus}
            />
          </div>
          <MDBInput
              type="text"
              key="Bill_Search_Input"
              label="Search Bills..."
              value={searchBill}
              onChange={(e) => setSearchBill(e.target.value)}
          />
          <div
              style={{
                maxHeight: "200px",
                overflowY: "auto",
                border: "1px solid #ddd",
                padding: "10px"
              }}
          >
            {billList
                .filter((bill) => {
                  const selectedTypes = selectedBillTypes
                      .filter((selectedType) => selectedType.enum && selectedType.enum !== "")
                        .map((selected) => selected.enum);
                  // If at least one bill type is selected, only allow bills whose
                  // billNumber starts with one of the selected types.
                  if (selectedTypes.length > 0) {
                    const billPrefix = bill.billNumber.split(" ")[0];
                    const billBypass = selectedBills.some((selected) => bill.billNumber === selected.billNumber)
                    return selectedTypes.includes(billPrefix) || billBypass;
                  }
                  return true;
                })
                // Then filter by search text (billNumber or shortTitle)
                .filter(
                    (bill) =>
                        bill.billNumber
                            .toLowerCase()
                            .includes(searchBill.toLowerCase()) ||
                        bill.shortTitle
                            .toLowerCase()
                            .includes(searchBill.toLowerCase())
                )
                .map((bill) => (
                    <div title={`${bill.billNumber}: ${bill.shortTitle}`} key={`div_${bill.billNumber}`}>
                      <MDBCheckbox
                          key={`${bill.billNumber}: ${bill.shortTitle}`}
                          label={`${bill.billNumber}`}
                          onChange={() => handleBillChange(bill)}
                          checked={selectedBills.some(
                              (selected) => selected.billNumber === bill.billNumber
                          )}
                      />
                    </div>
                ))}
          </div>
        </li>
      </MDBSideNavCollapse>
    </MDBSideNavItem>
);
};

export default BillFilter;
